import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { api } from "../../../env"
import { MatchingState } from "../../../utils/enums"
import ModePickerCard from "./matching/ModePickerCard"
import ProposedMatchesCard from "./matching/ProposedMatchesCard"
import Loading from "../../../components/loading"
import { ManualMatchingPanel } from "../ManualMatching"

export default function MatchingTab({ program }) {

    const navigate = useNavigate()
    const programId = program?.id
    const autoEnabled = !!program?.questionnaire

    const [mode, setMode] = useState('auto')
    const [waitingMenteeCount, setWaitingMenteeCount] = useState(null)
    const [waitingMentorCount, setWaitingMentorCount] = useState(null)
    const [refreshToken, setRefreshToken] = useState(0)
    const [initialLoad, setInitialLoad] = useState(true)
    const [running, setRunning] = useState(false)
    const [actingOn, setActingOn] = useState(null)

    useEffect(() => {
        if (!programId) return
        setInitialLoad(true)
        fetchWaitingCount().finally(() => setInitialLoad(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programId])

    useEffect(() => {
        if (mode === 'auto' && !autoEnabled) setMode('manual')
    }, [autoEnabled, mode])

    const fetchWaitingCountFor = (type, setter) => api
        .get(`programs/${programId}/participants`, {
            params: { status: MatchingState.WAITING, type, limit: 1, page: 1 }
        })
        .then(res => setter(res.data?.meta?.total ?? 0))
        .catch(() => setter(0))

    const fetchWaitingCount = () => Promise.all([
        fetchWaitingCountFor('Mentee', setWaitingMenteeCount),
        fetchWaitingCountFor('Mentor', setWaitingMentorCount),
    ])

    const refreshProposed = () => setRefreshToken(t => t + 1)

    const runAlgorithm = () => {
        if (!programId || running) return
        setRunning(true)
        api.post(`programs/${programId}/trigger-matching`)
            .then(() => {
                refreshProposed()
                return fetchWaitingCount()
            })
            .catch(() => { /* axios interceptor already surfaces a toast */ })
            .finally(() => setRunning(false))
    }

    const approve = (matchId) => {
        if (actingOn) return
        setActingOn(matchId)
        api.post(`matches/${matchId}/approve`)
            .then(() => {
                refreshProposed()
                return fetchWaitingCount()
            })
            .catch(() => { /* axios interceptor already surfaces a toast */ })
            .finally(() => setActingOn(null))
    }

    const reject = (matchId) => {
        if (actingOn) return
        setActingOn(matchId)
        api.post(`matches/${matchId}/reject-preview`)
            .then(() => {
                refreshProposed()
                return fetchWaitingCount()
            })
            .catch(() => { /* axios interceptor already surfaces a toast */ })
            .finally(() => setActingOn(null))
    }

    if (initialLoad) {
        return <Loading message="Loading matching..." />
    }

    return (
        <div className="flex columns gap-m">
            <ModePickerCard
                menteeCount={waitingMenteeCount}
                mentorCount={waitingMentorCount}
                mode={mode}
                onModeChange={setMode}
                onRunAlgorithm={runAlgorithm}
                autoEnabled={autoEnabled}
                running={running}
            />

            {mode === 'auto' && (
                <ProposedMatchesCard
                    programId={programId}
                    actingOn={actingOn}
                    onApprove={approve}
                    onReject={reject}
                    refreshToken={refreshToken}
                />
            )}

            {mode === 'manual' && (
                <div className="card standard-padding-s">
                    <ManualMatchingPanel
                        programId={programId}
                        program={program}
                        exploreTrack={(trackId) => navigate(`../track/${trackId}`)}
                    />
                </div>
            )}
        </div>
    )
}
